/**
 * 남기기 시트 — 기록 한 건을 올립니다.
 *
 * 세 가지를 없앴습니다(기획서 6장).
 *   1. 장소 고르기 — 앱이 이미 압니다. 틀렸을 때만 `장소 바꾸기` 로 손댑니다
 *   2. 별점 — 접어 둡니다. 일기에 별점을 물으면 손이 멈춥니다
 *   3. 글 강요 — 사진만 올려도 됩니다. 가장 흔한 행동이 가장 짧아야 합니다
 *
 * 사진 1장 또는 글 1자만 있으면 올라갑니다.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pickPhotoFile, shouldTellUser } from 'common/utils/photoPick';
import { isNativeApp } from 'common/webview';
import { useAlert } from 'contexts/AlertContext';
import { Button, Chip, Sheet, StarInput, TextArea } from 'components/ds';
import { IcPlus, IcStarOutline, IcTrash } from 'components/common/icons/EmojiIcons';
import styles from './JournalSheet.module.css';

export interface JournalAnchor {
    placeId: string;
    placeName?: string | null;
    /** 여행 N일차 — 일반 모임은 없습니다 */
    dayNumber?: number | null;
    /** meeting_sub.sub_id */
    subId?: number | null;
    /** `2일차 · 첨성대` / `2차 · 역삼 포차` 처럼 이미 다듬어진 라벨 */
    label?: string | null;
}

interface Props {
    open: boolean;
    onClose: () => void;
    anchor: JournalAnchor | null;
    /** 사진 버튼으로 들어온 경우 미리 채워집니다 */
    initialPhotos?: File[];
    /** 장소를 바꿀 수 있는 경우에만 넘깁니다 */
    onChangeAnchor?: () => void;
    busy?: boolean;
    onSubmit: (draft: { content: string; rating: number; photos: File[] }) => void;
}

export const JournalSheet: React.FC<Props> = ({
    open, onClose, anchor, initialPhotos, onChangeAnchor, busy, onSubmit,
}) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [content, setContent] = useState('');
    const [rating, setRating] = useState(0);
    const [showRating, setShowRating] = useState(false);
    const [photos, setPhotos] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const fileRef = useRef<HTMLInputElement>(null);

    /* 시트를 열 때마다 처음 상태로. 닫고 다시 열었는데 지난 글이 남아 있으면
       그대로 올려 버리는 사고가 납니다. */
    useEffect(() => {
        if (!open) return;
        setContent('');
        setRating(0);
        setShowRating(false);
        setPhotos(initialPhotos || []);
    }, [open, initialPhotos]);

    /* 미리보기 URL 은 직접 거둬들여야 합니다 — 안 그러면 메모리에 쌓입니다 */
    useEffect(() => {
        const urls = photos.map(f => URL.createObjectURL(f));
        setPreviews(urls);
        return () => { urls.forEach(URL.revokeObjectURL); };
    }, [photos]);

    const addPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length > 0) setPhotos(prev => [...prev, ...files]);
    };

    const removePhoto = (idx: number) => {
        setPhotos(prev => prev.filter((_, i) => i !== idx));
    };

    const canSubmit = !busy && (photos.length > 0 || content.trim().length > 0);

    return (
        <Sheet open={open} onClose={onClose} title={t('journal.sheetTitle')}>
            {/* 앵커 — 앱이 정한 값을 보여주고, 틀렸을 때만 손대게 합니다 */}
            {anchor && (
                <div className={styles.anchor}>
                    <Chip label={anchor.label || anchor.placeName || ''} selected />
                    {onChangeAnchor && (
                        <button type="button" className={styles.changeBtn} onClick={onChangeAnchor}>
                            {t('journal.changePlace')}
                        </button>
                    )}
                </div>
            )}

            {/* 사진 — 첫 칸이 추가 버튼이라 자리를 찾지 않습니다 */}
            <div className={styles.photos}>
                <button
                    type="button"
                    className={styles.addPhoto}
                    onClick={() => {
                        /* 네이티브인지 **동기로 먼저** 가릅니다. await 뒤에는 사용자
                           제스처가 끝나 웹뷰가 input.click() 을 무시합니다 — 갤러리를
                           닫고 돌아왔는데 아무 일도 없던 이유입니다. */
                        if (!isNativeApp()) { fileRef.current?.click(); return; }
                        void (async () => {
                            const res = await pickPhotoFile();
                            if (res.ok) { setPhotos(prev => [...prev, res.file]); return; }
                            if (shouldTellUser(res.reason)) {
                                showAlert({ message: t('journal.photoPickFailed') });
                            }
                        })();
                    }}
                    disabled={busy}
                >
                    <IcPlus size={18} />
                    <span>{t('journal.photo')}</span>
                </button>
                {previews.map((url, i) => (
                    <span key={url} className={styles.thumb}>
                        <img src={url} alt="" />
                        <button
                            type="button"
                            className={styles.removeBtn}
                            onClick={() => removePhoto(i)}
                            aria-label={t('common.delete')}
                        >
                            <IcTrash size={12} />
                        </button>
                    </span>
                ))}
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={addPhotos} />
            </div>

            {/* 글 — 선택임을 글자로 명시합니다. 필수로 보이면 안 씁니다 */}
            <TextArea
                label={t('journal.noteLabel')}
                optionalText={t('journal.noteOptional')}
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={4}
                maxLength={500}
                showCount
            />

            {/* 별점은 접어 둡니다 */}
            {showRating ? (
                <div className={styles.ratingOpen}>
                    <StarInput value={rating} onChange={setRating} />
                </div>
            ) : (
                <button
                    type="button"
                    className={styles.ratingToggle}
                    onClick={() => setShowRating(true)}
                >
                    <IcStarOutline size={16} />
                    <span className={styles.ratingLabel}>{t('journal.addRating')}</span>
                    <span className={styles.optional}>{t('journal.optional')}</span>
                </button>
            )}

            <div className={styles.footer}>
                {/* 왜 못 누르는지 말해 줍니다. 회색 버튼만 있으면 고장으로 보입니다 —
                   실제로 "눌러도 반응이 없다" 는 신고가 여기서 나왔습니다. */}
                {!canSubmit && !busy && (
                    <p className={styles.hint}>{t('journal.needSomething')}</p>
                )}
                <Button
                    variant="primary"
                    size="lg"
                    block
                    disabled={!canSubmit}
                    onClick={() => onSubmit({ content: content.trim(), rating, photos })}
                >
                    {busy ? t('journal.submitting') : t('journal.submit')}
                </Button>
            </div>
        </Sheet>
    );
};
